import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Function to check if token is expired
function isTokenExpired(token) {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user") || "null"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check token validity on mount and set authentication state
  useEffect(() => {
    if (token && !isTokenExpired(token)) {
      localStorage.setItem("token", token);
      setIsAuthenticated(true);
    } else if (token && isTokenExpired(token)) {
      // Token is expired, logout
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      setToken(null);
      setUser(null);
    } else {
      // No token
      setIsAuthenticated(false);
    }
  }, [token]);

  // Auto-logout check every 30 seconds
  useEffect(() => {
    const checkTokenExpiry = () => {
      const currentToken = localStorage.getItem('token');
      if (currentToken && isTokenExpired(currentToken)) {
        // Clear everything
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        // Force redirect
        window.location.href = '/login';
      }
    };
    
    // Check immediately
    checkTokenExpiry();
    
    // Then check every 30 seconds
    const interval = setInterval(checkTokenExpiry, 30000);

    return () => clearInterval(interval);
  }, []); // Empty deps to run only once

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    }
  }, [user]);

  const login = (token, userData) => {
    // Validate token before setting
    if (token && !isTokenExpired(token)) {
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));
      setToken(token);
      setUser(userData);
      setIsAuthenticated(true);
    } else {
      console.error('Attempted to login with invalid/expired token');
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
  };

  const value = {
    token,
    user,
    isAuthenticated,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
